import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import api from '../api/client'
import { clearAuthSessionStorage } from '../utils/authSessionCleanup'
import { parseStoredUser } from '../utils/authSession'

export function ProtectedRoute({ children }) {
  const { getToken } = useAuth()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(true)
  const [isAuth, setIsAuth] = useState(false)

  useEffect(() => {
    let active = true

    async function verify() {
      const token = getToken()
      if (!token) {
        if (!active) return
        setIsAuth(false)
        setIsLoading(false)
        return
      }

      try {
        await api.get('/auth/me')
        if (!active) return
        setIsAuth(true)
      } catch {
        clearAuthSessionStorage()
        if (!active) return
        setIsAuth(false)
      } finally {
        if (active) setIsLoading(false)
      }
    }

    verify()
    return () => {
      active = false
    }
  }, [getToken])

  if (isLoading) {
    return <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>Loading...</div>
  }

  if (!isAuth) {
    if (location.pathname.startsWith('/self-service')) {
      return <Navigate to="/self-service/login" replace />
    }
    return <Navigate to="/login" replace />
  }

  // Restrict employee-role sessions strictly to the Employee Self-Service portal
  const user = parseStoredUser()
  const isEmployee = user?.role === 'employee' || user?.is_employee
  if (isEmployee) {
    if (location.pathname === '/' || (!location.pathname.startsWith('/self-service') && !location.pathname.startsWith('/support'))) {
      return <Navigate to="/self-service/dashboard" replace />
    }
  } else {
    // Restrict normal admin/staff sessions away from the employee self-service portal
    if (location.pathname.startsWith('/self-service')) {
      return <Navigate to="/dashboard/personal" replace />
    }
  }

  return children
}

export function PublicOnlyRoute({ children, redirectPath = null }) {
  const { getToken } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [isAuth, setIsAuth] = useState(false)

  useEffect(() => {
    let active = true

    async function verify() {
      const token = getToken()
      if (!token) {
        if (!active) return
        setIsAuth(false)
        setIsLoading(false)
        return
      }
      try {
        await api.get('/auth/me')
        if (!active) return
        setIsAuth(true)
      } catch {
        clearAuthSessionStorage()
        if (!active) return
        setIsAuth(false)
      } finally {
        if (active) setIsLoading(false)
      }
    }

    verify()
    return () => {
      active = false
    }
  }, [getToken])

  if (isLoading) {
    return <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>Loading...</div>
  }

  if (isAuth) {
    const user = parseStoredUser()
    const isEmployee = user?.role === 'employee' || user?.is_employee
    const target = redirectPath || (isEmployee ? '/self-service/dashboard' : '/dashboard/personal')
    return <Navigate to={target} replace />
  }

  return children
}
